import { api } from './client';

/**
 * SIP-Zugang einer Kampagne (06_telephony_sip_inbound.md).
 *
 * Das Passwort kommt nie zurück - nur die Angabe, ob eines hinterlegt ist.
 */
export interface SipConfigView {
  host: string;
  username: string;
  hasPassword: boolean;
  port: number | null;
  transport: 'udp' | 'tcp' | 'tls' | null;
  authId: string | null;
  realm: string | null;
  /** Nur informativ - es gibt keinen anbieterspezifischen Code. */
  providerName: string | null;
  registrationEnabled: boolean;
  fromDomain: string | null;
  registrationExpiration: number | null;
  registrationRetryInterval: number | null;
  /** Nur bei individuellen Zugängen je Caller. */
  userId?: string;
}

export interface SipConfigWriteInput {
  host: string;
  username: string;
  /** Leer lassen, um das gespeicherte Passwort zu behalten. */
  password?: string;
  port?: number | null;
  transport?: 'udp' | 'tcp' | 'tls' | null;
  authId?: string | null;
  realm?: string | null;
  providerName?: string | null;
  registrationEnabled?: boolean;
  fromDomain?: string | null;
  registrationExpiration?: number | null;
  registrationRetryInterval?: number | null;
}

/**
 * Ergebnis von „Verbindung testen".
 *
 * Die Oberfläche übersetzt diese Werte in Alltagssprache; rohe SIP- oder
 * Asterisk-Meldungen erscheinen dort nie.
 */
export type SipTestResult =
  | 'ok'
  | 'registered'
  | 'registration_failed'
  | 'rejected'
  | 'unreachable'
  | 'timeout';

const pfad = (campaignId: string): string => '/campaigns/' + campaignId + '/sip';

export const sipApi = {
  get: (campaignId: string) =>
    api.get<{ global: SipConfigView | null; individual: SipConfigView[] }>(pfad(campaignId)),

  saveGlobal: (campaignId: string, input: SipConfigWriteInput) =>
    api.put<{ config: SipConfigView }>(pfad(campaignId), input),

  removeGlobal: (campaignId: string) => api.delete<{ ok: true }>(pfad(campaignId)),

  saveForUser: (campaignId: string, userId: string, input: SipConfigWriteInput) =>
    api.put<{ config: SipConfigView }>(pfad(campaignId) + '/users/' + userId, input),

  removeForUser: (campaignId: string, userId: string) =>
    api.delete<{ ok: true }>(pfad(campaignId) + '/users/' + userId),

  /**
   * Der Test kann länger dauern: bei aktivierter Registrierung wartet der
   * Server darauf, dass der Anbieter antwortet.
   */
  test: (campaignId: string, userId?: string) =>
    api.post<{ result: SipTestResult }>(pfad(campaignId) + '/test', userId ? { userId } : {}),
};
